import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Pagination, Select, Space, message } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Permission } from '@aid/shared';
import type { UserResponse } from '../../../api/types';
import { AdminListToolbar } from '../../../components/AdminListToolbar';
import { DataTable } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useT } from '../../../locales';
import { useDebouncedValue } from '../../../lib/useDebouncedValue';
import { useViewport } from '../../../lib/useViewport';
import { paginationProps } from '../../../lib/paginacion';
import { serverSortProps, useServerPaginacion } from '../../../lib/server-sort';
import { useDeleteUser, useUsersList } from '../api/useUsers';
import { DeleteUserConfirmModal } from './DeleteUserConfirmModal';
import { USER_FORM_MODE, type UserFormMode } from './UserForm';
import { UserFormModal } from './UserFormModal';
import { UsersTableActions } from './UsersTableActions';
import { UsersCardList } from './UsersCardList';
import { UserStatusTag } from './UserStatusTag';

const ACTIVE_FILTER = {
  ACTIVE: 'true',
  INACTIVE: 'false',
} as const;

type ActiveFilter = (typeof ACTIVE_FILTER)[keyof typeof ACTIVE_FILTER];

export function UsersTable() {
  const t = useT();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useViewport();
  const [search, setSearch] = useState('');
  const [isActive, setIsActive] = useState<ActiveFilter | undefined>();
  const [formMode, setFormMode] = useState<UserFormMode | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserResponse | null>(null);
  const [deleteUserTarget, setDeleteUserTarget] = useState<UserResponse | null>(null);
  const debouncedSearch = useDebouncedValue(search.trim());
  const { page, pageSize, sort, handleChange, setPage } = useServerPaginacion<UserResponse>(20);
  const deleteUser = useDeleteUser();

  const canCreate = useHasPermission(Permission.USERS_CREATE);
  const canUpdate = useHasPermission(Permission.USERS_UPDATE);
  const canReadRoles = useHasPermission(Permission.ROLES_READ);
  const canDelete = useHasPermission(Permission.USERS_DELETE);

  const { data, isLoading, isFetching, error, refetch } = useUsersList({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    search: debouncedSearch || undefined,
    isActive: isActive === undefined ? undefined : isActive === ACTIVE_FILTER.ACTIVE,
    ...sort,
  });

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, setPage]);

  const clearFilters = () => {
    setSearch('');
    setIsActive(undefined);
    setPage(1);
  };

  const closeFormModal = () => {
    setFormMode(null);
    setSelectedUser(null);
  };

  const handleDelete = async () => {
    if (!deleteUserTarget) return;

    try {
      await deleteUser.mutateAsync(deleteUserTarget.id);
      message.success(t('pages.users.deactivateSuccess'));
      setDeleteUserTarget(null);
    } catch {
      message.error(t('pages.users.deactivateError'));
    }
  };

  const hasFilters = Boolean(debouncedSearch || isActive !== undefined);
  const total = data?.total ?? 0;
  const emptyState = (
    <EmptyState
      title={t(hasFilters ? 'pages.users.emptyFilteredTitle' : 'pages.users.emptyTitle')}
      description={t(
        hasFilters ? 'pages.users.emptyFilteredDescription' : 'pages.users.emptyDescription',
      )}
      action={hasFilters ? <Button onClick={clearFilters}>{t('common.clear')}</Button> : undefined}
    />
  );

  if (error) {
    return (
      <EmptyState
        title={t('pages.users.errorLoad')}
        description={t('common.error')}
        action={<Button onClick={() => void refetch()}>{t('common.retry')}</Button>}
      />
    );
  }

  // Un solo cableado de acciones para la tabla y las tarjetas de móvil: los
  // permisos no pueden divergir entre las dos vistas.
  const listActions = {
    canUpdate,
    canReadRoles,
    canDelete,
    onDetail: (userId: string) => navigate(`/users/${userId}`),
    onEdit: (user: UserResponse) => {
      setSelectedUser(user);
      setFormMode(USER_FORM_MODE.EDIT);
    },
    onManageRoles: (userId: string) => navigate(`/users/${userId}/edit`),
    onDelete: setDeleteUserTarget,
  };

  const columns = [
    {
      title: t('pages.users.email'),
      dataIndex: 'email' as keyof UserResponse,
      key: 'email',
      ...serverSortProps('email', sort),
    },
    {
      title: t('pages.users.name'),
      key: 'name',
      ...serverSortProps('name', sort),
      render: (_value: unknown, record: UserResponse) =>
        [record.firstName, record.lastName].filter(Boolean).join(' ') || '-',
    },
    {
      title: t('pages.users.username'),
      dataIndex: 'username' as keyof UserResponse,
      key: 'username',
      ...serverSortProps('username', sort),
    },
    {
      title: t('pages.users.department'),
      dataIndex: 'department' as keyof UserResponse,
      key: 'department',
      render: (value: string | null) => value ?? '-',
    },
    {
      title: t('pages.users.status'),
      dataIndex: 'isActive' as keyof UserResponse,
      key: 'isActive',
      ...serverSortProps('isActive', sort),
      render: (active: boolean) => <UserStatusTag isActive={active} />,
    },
    {
      title: t('pages.users.roles'),
      key: 'roles',
      render: (_value: unknown, record: UserResponse) => (
        <Space size={[0, 4]} wrap>
          {record.roles.map((role) => (
            <span key={role.id}>{role.name}</span>
          ))}
          {record.roles.length === 0 && '-'}
        </Space>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 64,
      render: (_value: unknown, record: UserResponse) => (
        <UsersTableActions user={record} {...listActions} />
      ),
    },
  ];

  return (
    <>
      <AdminListToolbar
        className="users-toolbar"
        filters={
          <>
            <Input
              aria-label={t('pages.users.searchPlaceholder')}
              placeholder={t('pages.users.searchPlaceholder')}
              prefix={<SearchOutlined />}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              allowClear
              style={{ width: 280, maxWidth: '100%' }}
            />
            <Select<ActiveFilter>
              aria-label={t('pages.users.filterStatus')}
              placeholder={t('pages.users.filterStatus')}
              allowClear
              value={isActive}
              onChange={(value) => {
                setIsActive(value);
                setPage(1);
              }}
              options={[
                { label: t('pages.users.activo'), value: ACTIVE_FILTER.ACTIVE },
                { label: t('pages.users.inactivo'), value: ACTIVE_FILTER.INACTIVE },
              ]}
              style={{ width: 180, maxWidth: '100%' }}
            />
          </>
        }
        actions={
          canCreate ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setSelectedUser(null);
                setFormMode(USER_FORM_MODE.CREATE);
              }}
            >
              {t('pages.users.create')}
            </Button>
          ) : undefined
        }
      />
      {isMobile ? (
        <>
          <UsersCardList
            users={data?.data ?? []}
            loading={isLoading || isFetching}
            empty={emptyState}
            {...listActions}
          />
          {total > 0 && (
            <div className="users-card-list__pagination">
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                simple
                showSizeChanger={false}
                onChange={setPage}
              />
            </div>
          )}
        </>
      ) : (
        <DataTable<UserResponse>
          className="users-table"
          // `max-content` keeps every column on one line, which at 768px pushes
          // the last column past the container and clips its header. On tablet
          // the columns wrap instead, so all seven stay readable without a
          // sideways scroll.
          scroll={isTablet ? { x: '100%' } : undefined}
          columns={columns}
          dataSource={data?.data ?? []}
          rowKey="id"
          loading={isLoading || isFetching}
          pagination={paginationProps(t, {
            page,
            pageSize,
            total,
          })}
          onChange={handleChange}
          locale={{ emptyText: emptyState }}
        />
      )}
      {formMode && (
        <UserFormModal open mode={formMode} user={selectedUser} onClose={closeFormModal} />
      )}
      {deleteUserTarget && (
        <DeleteUserConfirmModal
          open
          onConfirm={handleDelete}
          onCancel={() => setDeleteUserTarget(null)}
          loading={deleteUser.isPending}
        />
      )}
    </>
  );
}
